import { useState, useMemo } from 'react';
import { useLocation } from 'wouter';
import {
  Search,
  ChevronRight,
  Sparkles,
  ExternalLink,
  Loader2,
  Archive,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useGetArtefacts, useGetArtefact, axiosInstance } from '@edison/api-client-react';
import { Button } from '@/components/ui/button.js';
import { SlidePanel } from '@/components/ui/slide-panel.js';
import { MarkdownContent } from '@/components/ui/markdown-content.js';
import { FilterChipGroup } from '@/components/ui/filter-chip.js';
import { EmptyState } from '@/components/ui/empty-state.js';
import { InlineError } from '@/components/ui/inline-error.js';
import { SkeletonRow } from '@/components/ui/skeleton.js';
import { UploadZone } from '@/components/evidence/upload-zone.js';
import { ConfirmCard, type PendingArtefact } from '@/components/evidence/confirm-card.js';

// ─── Type badge config ────────────────────────────────────────────────────────

interface TypeBadge {
  label: string;
  bg: string;
  text: string;
}

const TYPE_BADGE: Record<string, TypeBadge> = {
  github_commit:      { label: 'Commit',      bg: 'bg-[oklch(0.93_0.015_250)]', text: 'text-[oklch(0.42_0.04_250)]' },
  github_pr:          { label: 'Pull request', bg: 'bg-[oklch(0.93_0.015_250)]', text: 'text-[oklch(0.42_0.04_250)]' },
  github_issue:       { label: 'Issue',        bg: 'bg-[oklch(0.93_0.015_250)]', text: 'text-[oklch(0.42_0.04_250)]' },
  meeting_transcript: { label: 'Transcript',   bg: 'bg-[oklch(0.94_0.04_275)]', text: 'text-[oklch(0.40_0.12_270)]' },
  prd_document:       { label: 'PRD',          bg: 'bg-[oklch(0.95_0.04_65)]',  text: 'text-[oklch(0.48_0.14_60)]' },
  llm_session:        { label: 'AI session',   bg: 'bg-[oklch(0.93_0.04_295)]', text: 'text-[oklch(0.40_0.14_290)]' },
  abandoned_work:     { label: 'Abandoned',    bg: 'bg-[oklch(0.94_0.04_25)]',  text: 'text-[oklch(0.48_0.18_22)]' },
  market_search:      { label: 'Market',       bg: 'bg-[oklch(0.94_0.04_155)]', text: 'text-[oklch(0.42_0.14_155)]' },
  manual_note:        { label: 'Note',         bg: 'bg-[oklch(0.95_0.006_250)]', text: 'text-[oklch(0.55_0.01_250)]' },
  file_upload:        { label: 'File',         bg: 'bg-[oklch(0.95_0.006_250)]', text: 'text-[oklch(0.55_0.01_250)]' },
};

function getTypeBadge(type: string): TypeBadge {
  return TYPE_BADGE[type] ?? { label: type, bg: 'bg-surface-2', text: 'text-dim' };
}

// ─── Filter chips ─────────────────────────────────────────────────────────────

const FILTER_CHIPS = [
  { key: 'all',           label: 'All',           typeFilter: undefined as string | undefined },
  { key: 'abandoned_work', label: 'Abandoned work', typeFilter: 'abandoned_work' },
  { key: 'meeting_transcript', label: 'Transcripts', typeFilter: 'meeting_transcript' },
  { key: 'github_commit', label: 'Commits',       typeFilter: 'github_commit' },
  { key: 'llm_session',   label: 'Sessions',      typeFilter: 'llm_session' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

// ─── Slide panel content ──────────────────────────────────────────────────────

function ArtefactPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const [, navigate] = useLocation();
  const { data, isLoading, error } = useGetArtefact(id);
  const raw = data?.data as unknown as {
    id: string; type: string; title?: string; createdAt: string; sourceDate?: string | null;
    projectId?: string | null; content?: string; contentHash?: string;
  } | undefined;

  const isCodeType = raw?.type
    ? ['github_commit', 'github_pr', 'github_issue'].includes(raw.type)
    : false;

  return (
    <div className="p-5 space-y-4">
      {isLoading && (
        <div className="space-y-3">
          <div className="h-5 w-2/3 skeleton rounded" />
          <div className="h-4 w-1/3 skeleton rounded" />
          <div className="h-40 skeleton rounded" />
        </div>
      )}
      {error != null && (
        <p className="text-sm text-destructive">Failed to load artefact.</p>
      )}
      {raw && (
        <>
          <div className="space-y-1.5">
            <div className="flex items-start gap-2">
              {(() => {
                const badge = getTypeBadge(raw.type);
                return (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium shrink-0 mt-0.5 ${badge.bg} ${badge.text}`}>
                    {badge.label}
                  </span>
                );
              })()}
              <h3 className="text-[14px] font-semibold text-ink leading-snug">{raw.title}</h3>
            </div>
            <div className="flex items-center gap-3 text-[11.5px] text-dim">
              <span>{formatDate(raw.sourceDate ?? raw.createdAt)}</span>
              {raw.contentHash && (
                <span className="font-mono truncate max-w-[140px]" title={raw.contentHash}>
                  #{raw.contentHash.slice(0, 8)}
                </span>
              )}
            </div>
          </div>

          {raw.content && (
            isCodeType ? (
              <div className="rounded-lg p-4 border border-hairline text-[12.5px] leading-relaxed max-h-96 overflow-y-auto font-mono bg-[oklch(0.97_0.006_250)] text-foreground whitespace-pre-wrap break-all">
                {raw.content}
              </div>
            ) : (
              <div className="rounded-lg p-4 border border-hairline bg-card overflow-y-auto max-h-[calc(100vh-280px)]">
                <MarkdownContent>{raw.content}</MarkdownContent>
              </div>
            )
          )}

          {raw.projectId && (
            <button
              onClick={() => { onClose(); navigate(`/projects/${raw.projectId}`); }}
              className="flex items-center gap-1.5 text-[12px] text-primary hover:underline"
            >
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
              View project
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─── EvidencePage ─────────────────────────────────────────────────────────────

export function EvidencePage() {
  const [, navigate] = useLocation();
  const [search, setSearch] = useState('');
  const [activeChip, setActiveChip] = useState('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: pendingData } = useQuery({
    queryKey: ['artefacts', 'pending_confirmation'],
    queryFn: () =>
      axiosInstance
        .get<{ data: { items: PendingArtefact[] } }>('/api/artefacts', { params: { uploadStatus: 'pending_confirmation' } })
        .then((r) => r.data.data.items),
    refetchInterval: 8000,
    refetchIntervalInBackground: false,
  });

  const { data: processingData } = useQuery({
    queryKey: ['artefacts', 'processing'],
    queryFn: () =>
      axiosInstance
        .get<{ data: { items: Array<{ id: string; uploadFilename?: string | null }> } }>('/api/artefacts', { params: { uploadStatus: 'processing' } })
        .then((r) => r.data.data.items),
    refetchInterval: 8000,
    refetchIntervalInBackground: false,
  });

  const pendingArtefacts = pendingData ?? [];
  const processingArtefacts = processingData ?? [];

  function handleUploadStarted() {
    void queryClient.invalidateQueries({ queryKey: ['artefacts', 'processing'] });
  }

  function handleConfirmed() {
    void queryClient.invalidateQueries({ queryKey: ['artefacts'] });
  }

  function handleDiscard(id: string) {
    void axiosInstance.delete(`/api/artefacts/${id}`).catch(() => null);
    void queryClient.invalidateQueries({ queryKey: ['artefacts'] });
  }

  const chip = FILTER_CHIPS.find(c => c.key === activeChip) ?? FILTER_CHIPS[0]!;

  const { data, isLoading, error, refetch } = useGetArtefacts(
    chip.typeFilter ? { type: chip.typeFilter } : undefined
  );

  const allItems = data?.data?.items ?? [];
  const total = (data?.data as unknown as { pagination?: { total?: number } })?.pagination?.total ?? allItems.length;

  const items = useMemo(() => {
    if (!search.trim()) return allItems;
    const q = search.toLowerCase();
    return (allItems as Array<{ title?: string; content?: string }>)
      .filter(a => (a.title ?? '').toLowerCase().includes(q) || (a.content ?? '').toLowerCase().includes(q)) as typeof allItems;
  }, [allItems, search]);

  const handleAskEdison = () => {
    const prompt = search.trim()
      ? `Find me evidence related to: ${search}`
      : 'Help me find evidence in my vault';
    navigate(`/?q=${encodeURIComponent(prompt)}`);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Evidence</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Every artefact captured across projects. Search full-text, filter, or ask Edison to find what you need.
        </p>
      </div>

      {/* Upload zone */}
      <UploadZone onUploadStarted={handleUploadStarted} />

      {/* Search row */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search evidence by title or content…"
            className="w-full pl-9 pr-4 py-2.5 bg-card border border-hairline rounded-lg text-sm placeholder:text-muted-foreground outline-none focus:border-primary/40 transition-colors"
          />
        </div>
        <button
          onClick={handleAskEdison}
          className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-lg border border-hairline bg-card text-[13px] text-foreground font-medium hover:bg-surface-3 transition-colors shrink-0"
        >
          <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
          Ask Edison to find
        </button>
      </div>

      {/* Filter chips */}
      <FilterChipGroup
        chips={FILTER_CHIPS.map(c => ({
          key: c.key,
          label: c.label,
          count: activeChip === c.key && !isLoading ? total : undefined,
        }))}
        value={activeChip}
        onChange={setActiveChip}
      />

      {/* Confirm cards for pending extractions */}
      {pendingArtefacts.length > 0 && (
        <div className="space-y-3">
          {pendingArtefacts.map((a) => (
            <ConfirmCard
              key={a.id}
              artefact={a}
              onConfirmed={handleConfirmed}
              onDiscard={() => handleDiscard(a.id)}
            />
          ))}
        </div>
      )}

      {/* In-progress upload rows */}
      {processingArtefacts.length > 0 && (
        <div className="space-y-1">
          {processingArtefacts.map((a) => (
            <div
              key={a.id}
              className="flex items-center gap-3 rounded-lg border border-hairline bg-surface-2/50 px-4 py-3 text-[13px] text-dim"
            >
              <Loader2 className="h-4 w-4 animate-spin text-[oklch(0.55_0.18_270)]" aria-hidden="true" />
              <span className="flex-1">{a.uploadFilename ?? 'Uploading…'}</span>
              <span className="text-[11.5px]">Analysing…</span>
            </div>
          ))}
        </div>
      )}

      {/* Subheader */}
      {!isLoading && !error && items.length > 0 && (
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-dim">
          Most recent · showing {items.length}{total > items.length ? ` of ${total}` : ''}
        </p>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)}
        </div>
      )}

      {/* Error */}
      {error != null && (
        <InlineError message="Failed to load evidence." onRetry={() => refetch()} />
      )}

      {/* Empty */}
      {!isLoading && !error && items.length === 0 && (
        search ? (
          <EmptyState
            icon={<Archive className="h-10 w-10" />}
            title="No evidence matches your search"
            action={
              <button onClick={() => setSearch('')} className="text-sm text-primary hover:underline">
                Clear search
              </button>
            }
          />
        ) : (
          <EmptyState
            icon={<Archive className="h-10 w-10" />}
            title={activeChip === 'all' ? 'No evidence yet' : `No ${FILTER_CHIPS.find(c => c.key === activeChip)?.label.toLowerCase() ?? activeChip} evidence`}
            body="Upload a document or connect a GitHub repo to begin capturing evidence."
            action={
              <Button variant="primary" size="sm" onClick={() => navigate('/signals')}>
                Connect signal source
              </Button>
            }
          />
        )
      )}

      {/* List */}
      {!isLoading && !error && items.length > 0 && (
        <div className="max-h-[560px] overflow-y-auto rounded-[14px] border border-border bg-card divide-y divide-border">
          {items.map(item => {
            const row = item as {
              id: string;
              type: string;
              title?: string;
              createdAt: string;
              sourceDate?: string | null;
              metadata?: { repoName?: string; projectName?: string } | null;
            };
            const badge = getTypeBadge(row.type);
            const repoName = row.metadata?.repoName ?? null;
            return (
              <button
                key={row.id}
                onClick={() => setSelectedId(row.id)}
                className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-surface-3 transition-colors group"
              >
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium shrink-0 ${badge.bg} ${badge.text}`}>
                  {badge.label}
                </span>
                <span className="flex-1 min-w-0 text-[13px] font-medium text-ink truncate">
                  {row.title ?? ''}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  {repoName && (
                    <span className="text-[11.5px] text-dim hidden sm:block">{repoName}</span>
                  )}
                  <span className="text-[11.5px] text-dim tabular-nums">
                    {formatDate(row.sourceDate ?? row.createdAt)}
                  </span>
                  <ChevronRight className="h-3.5 w-3.5 text-dim opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Load more hint */}
      {!isLoading && items.length >= 20 && total > items.length && (
        <p className="text-xs text-center text-dim">
          Showing {items.length} of {total} · use search or filters to narrow results
        </p>
      )}

      {/* Artefact slide panel */}
      <SlidePanel
        open={selectedId != null}
        onClose={() => setSelectedId(null)}
        title="Evidence detail"
        width="w-[520px]"
      >
        {selectedId && <ArtefactPanel id={selectedId} onClose={() => setSelectedId(null)} />}
      </SlidePanel>
    </div>
  );
}
